import * as React from 'react';
import type { createCalendar } from '@event-calendar/core';
import {
  calendarViewToLib,
  fromCalendarDate,
  toCalendarDate,
  type CalendarView,
} from '../lib/calendar-range.js';

export { calendarViewToLib };

/**
 * Kalendář makléře (krok CRM3) — obálka kolem `@event-calendar/core`.
 *
 * VZOR je Pobytník (`apps/admin/src/app/(dashboard)/calendar/CalendarWidget.tsx`):
 *  - knihovna se načítá AŽ v `useEffect` přes dynamický `import`, ne na
 *    úrovni modulu. Důvod dva: SSR/i18n a testy v jsdom. Kdyby se
 *    `createCalendar` zavolalo při importu, stránka by v testu spadla dřív,
 *    než by se vůbec vykreslil nadpis.
 *  - instance knihovny se drží v refu a data se do ní dostávají přes
 *    `setOption`, ne přes nový mount. Překreslení události jinak zruší
 *    rozdělané tažení myší a posune scroll.
 *  - `locale: 'cs'` a `firstDay: 1` (pondělí) — český týden.
 *
 * ROZHODNUTÍ CRM3-12 (když se knihovna nenačte, data nesmějí zmizet):
 * chyba importu kreslí `role="alert"` hlášku; data zůstanou vidět v
 * záložním seznamu, který kreslí stránka (`CalendarFallbackList`).
 * Kalendář je obrazovka, kde makléř zjišťuje, že zítra jde na prohlídku;
 * prázdná obrazovka s hláškou je horší než prostý seznam.
 */

export interface CalendarEventInput {
  readonly id: string;
  /** Sloupec kalendáře (makléř). `null` = událost bez makléře. */
  readonly resourceId: string | null;
  readonly title: string;
  readonly start: string;
  readonly end: string;
  /**
   * Krok KAL1: tón plochy karty je TŘÍDA z tokenů (`eventToneClass`), ne hex
   * ze serveru — jen tak se karta v tmavém režimu nerozsvítí. Druhá třída
   * označuje zrušenou událost.
   */
  readonly classNames: readonly string[];
  /** Místo konání (adresa zakázky) — třetí řádek karty, prázdné se nekreslí. */
  readonly location: string;
  /** false = knihovna událost nechá ležet (makléř bez práva). */
  readonly editable: boolean;
}

export interface CalendarResourceInput {
  readonly id: string;
  readonly title: string;
}

/** Podmnožina API knihovny, kterou tu potřebujeme (typ `Calendar` je Svelte-flavored). */
interface LibCalendar {
  setOption: (name: string, value: unknown) => unknown;
}

/**
 * Tvary z knihovny odvozené z JEJÍHO vlastního podpisu
 * (`Parameters<typeof createCalendar>`), ne opsané vedle. Kdyby knihovna
 * přejmenovala pole, tsc by to tady odmítl dřív než v běhu.
 */
type LibOptions = NonNullable<Parameters<typeof createCalendar>[2]>;
type LibEvents = NonNullable<LibOptions['events']>;
type LibResources = NonNullable<LibOptions['resources']>;

/**
 * Toolbar se nekreslí vůbec. Typ knihovny tu popírá `false` (deklarace
 * popisuje jen objekt se třemi částmi), ale runtime i dokumentace přijímají
 * `false` — Pobytník to taky posílá. Cast je na JEDNOM místě a je
 * okomentovaný, aby se neztratil.
 */
const NO_TOOLBAR = false as unknown as NonNullable<LibOptions['headerToolbar']>;

export interface CalendarWidgetProps {
  readonly events: readonly CalendarEventInput[];
  readonly resources: readonly CalendarResourceInput[];
  /**
   * Zobrazeni REOSu (`day`/`week`/`month`/`list`), NE view knihovny. Prevod
   * dela widget (`calendarViewToLib`) — typ je zuzeny zamerne.
   *
   * Krok FIX6 bod 3: dokud tu byl `string`, stranka posilala uz prevedene
   * `timeGridDay` a widget ho prevedl PODRUHE. `VIEW_MAP` klic `timeGridDay`
   * nezna, takze kazde zobrazeni spadlo na fallback `timeGridWeek` —
   * prepinac hlasil „Den", knihovna kreslila TYDEN (7 sloupcu po ~30 px na
   * mobilu). Se `CalendarView` tsc druhy prevod odmitne.
   */
  readonly view: CalendarView;
  readonly date: Date;
  readonly editable: boolean;
  readonly onEventClick: (id: string) => void;
  readonly onRangeSelect: (start: string, end: string, resourceId: string | null) => void;
  readonly onEventMove: (id: string, start: string, end: string, revert: () => void) => void;
  /** Změna počtu/makléře musí invalidovat cache knihovny; viz stránka. */
  readonly cacheKey?: string | undefined;
  /**
   * Krok FIX4 bod 4: knihovna má řádek „all-day" napevno v angličtině
   * (`createAllDayContent`, žádná `locale` volba ho nemění) — popisek musí
   * jít z i18n katalogu stránky, ne hardcoded v widgetu (CLAUDE.md §5).
   */
  readonly allDayLabel: string;
}

/**
 * Vstup stránky -> vstup knihovny. Je to JEDNA funkce, protože data jdou
 * dovnitř na dvou místech (mount a `setOption`) a dva převody by se rozešly.
 * `Date`, ne ISO řetězec: knihovna „Z“ nepozná (viz `toCalendarDate`).
 */
function toLibEvents(events: readonly CalendarEventInput[]): LibEvents {
  return events.map((event) => ({
    id: event.id,
    resourceId: event.resourceId,
    title: event.title,
    start: toCalendarDate(event.start),
    end: toCalendarDate(event.end),
    classNames: [...event.classNames],
    extendedProps: { location: event.location },
    editable: event.editable,
  })) as unknown as LibEvents;
}

/**
 * Obsah karty události: ČAS · NÁZEV · MÍSTO (zadání KAL1 bod 3).
 *
 * `domNodes`, ne `html`: název a adresa jsou data makléře a klienta, takže
 * se do sazby nesmí dostat jako HTML. Čas a název jdou na jeden řádek, aby
 * půlhodinová událost nebyla jen „11:00“ s uříznutým názvem (živé měření
 * 6. 10.: tři ze čtyř karet v týdnu měly název mimo kartu).
 */
export { toLibEvents };

export function calendarEventContent(info: {
  event: { title: unknown; extendedProps?: Record<string, unknown> };
  timeText: string;
}): { domNodes: Node[] } {
  const wrap = document.createElement('div');
  wrap.className = 'rh-ec-ev';

  const line = document.createElement('span');
  line.className = 'rh-ec-ev__line';
  const time = document.createElement('span');
  time.className = 'rh-ec-ev__time';
  time.textContent = info.timeText;
  const title = document.createElement('span');
  title.className = 'rh-ec-ev__title';
  const titleText = typeof info.event.title === 'string' ? info.event.title : '';
  title.textContent = titleText;
  line.append(time, title);
  wrap.append(line);

  const location = info.event.extendedProps?.['location'];
  const hasLocation = typeof location === 'string' && location !== '';
  if (hasLocation) {
    const place = document.createElement('span');
    place.className = 'rh-ec-ev__place';
    place.textContent = location;
    wrap.append(place);
  }

  /*
   * Krok UXF1 bod 1: na karte se nazev zkracuje vypustkou (ve sloupci tydne
   * je ~145 px, cely se tam nevejde nikdy) — CELY ho proto nese `title`,
   * takze makler ho precte najetim mysi, aniz by musel otevirat detail.
   * `textContent` zustava zkraceny: `title` je atribut, ne sazba.
   */
  wrap.title = [info.timeText, titleText, hasLocation ? location : '']
    .filter((part) => part !== '')
    .join(' · ');
  return { domNodes: [wrap] };
}

export function CalendarWidget(props: CalendarWidgetProps): React.JSX.Element {
  const { events, resources, view, date, editable, allDayLabel, onEventClick, onRangeSelect, onEventMove } = props;
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const calRef = React.useRef<LibCalendar | null>(null);
  const [failed, setFailed] = React.useState(false);

  // Ref na callbacky, aby se knihovna nemountovala při každé nové funkci
  // z renderu — to by zrušilo rozdělané tažení.
  const cbRef = React.useRef({ onEventClick, onRangeSelect, onEventMove });
  cbRef.current = { onEventClick, onRangeSelect, onEventMove };
  const propsRef = React.useRef({ events, resources, view, date, editable, allDayLabel });
  propsRef.current = { events, resources, view, date, editable, allDayLabel };

  React.useEffect(() => {
    const el = containerRef.current;
    if (el === null) return undefined;
    let active = true;

    void import('@event-calendar/core')
      .then(({ createCalendar, DayGrid, TimeGrid, ResourceTimeline, List, Interaction }) => {
        if (!active || !el.isConnected) return;
        const current = propsRef.current;
        const cal = createCalendar(
          el,
          [DayGrid, TimeGrid, ResourceTimeline, List, Interaction],
          {
            view: calendarViewToLib(current.view),
            date: current.date,
            events: toLibEvents(current.events),
            resources: current.resources as unknown as LibResources,
            selectable: true,
            // Globálně zapnuté tažení; KONKRÉTNÍ událost se dá zakázat přes
            // `editable: false` v jejím vstupu (= `canUpdate` ze serveru).
            // Vypnout to globálně by znemožnilo táhnout i cizí události.
            editable: true,
            firstDay: 1,
            locale: 'cs',
            height: 'auto',
            // Krok FIX4 bod 4: Den/Týden dostanou OMEZENOU výšku s vlastním
            // scrollem — jen tak má `scrollTime` co posunout. `auto` (celá
            // stránka se posouvá) zůstává pro Měsíc/Seznam/zdroje, kde
            // „pracovní doba vs. zbytek dne" nedává smysl.
            views: {
              timeGridDay: { height: '600px' },
              timeGridWeek: { height: '600px' },
            },
            // Toolbar kreslí REOS sám (předchozí/dnes/další + přepínač
            // zobrazení + filtry).
            headerToolbar: NO_TOOLBAR,
            // Krok FIX4 bod 4: řádek „all-day" v češtině (knihovna ho jinak
            // kreslí napevno anglicky, viz `allDayLabel` výše).
            allDayContent: current.allDayLabel,
            // Krok KAL1 bod 2: aktuální čas kreslí tenkou linku (token
            // `--ec-now-indicator-color`), aby šlo vidět, kde v dni makléř je.
            nowIndicator: true,
            /*
             * Krok KAL1 bod 3: výchozích 24 px na půlhodinu nestačí — do karty
             * půlhodinové události se vešel jen čas a NÁZEV MĚL NULOVOU VÝŠKU
             * (živé měření 6. 10., tři ze čtyř karet v týdnu). 30 px je
             * nejnižší výška, do které se na výchozím stupni písma (14 px,
             * řádek 1,35) vejde celý řádek „čas · název“ i s odsazením.
             *
             * Vyšší hodnota by nepomohla: v sedmisloupcovém týdnu má sloupec
             * ~145 px a celý název se do něj nevejde ani na tři řádky. Zato by
             * ubrala z viditelného výřezu dne (600 px = 10 hodin při 30 px),
             * takže by makléř odpolední prohlídku bez rolování neviděl.
             */
            slotHeight: 30,
            // Na kartě stojí ZAČÁTEK, ne „13:00–13:30": konec je vidět z výšky
            // karty a dvojice časů sežrala půlku řádku, takže z názvu zbylo
            // „Pod…" (měřeno 6. 10. na první verzi KAL1). Celý termín je
            // v agendě i v detailu události.
            displayEventEnd: false,
            // Záhlaví dne „po 5. 10.“ — den v týdnu zkráceně, pak datum.
            dayHeaderFormat: { weekday: 'short', day: 'numeric', month: 'numeric' },
            eventContent: calendarEventContent,
            // Celý den 0–24 h musí jít dosáhnout scrollem (makléř může mít
            // prohlídku brzy ráno nebo pozdě večer), výchozí pohled je ale
            // pracovní doba 7–20 h — ne knihovní výchozí 6:00.
            slotMinTime: '00:00:00',
            slotMaxTime: '24:00:00',
            scrollTime: '07:00:00',
            eventClick: (info: { event: { id: string | number } }) => {
              cbRef.current.onEventClick(String(info.event.id));
            },
            select: (info: {
              start: Date;
              end: Date;
              resource?: { id?: string | number } | undefined;
            }) => {
              cbRef.current.onRangeSelect(
                fromCalendarDate(info.start),
                fromCalendarDate(info.end),
                info.resource?.id === undefined ? null : String(info.resource.id),
              );
            },
            eventDrop: (info: { event: { id: string | number; start: Date; end: Date }; revert: () => void }) => {
              cbRef.current.onEventMove(
                String(info.event.id),
                fromCalendarDate(info.event.start),
                fromCalendarDate(info.event.end),
                info.revert,
              );
            },
            eventResize: (info: {
              event: { id: string | number; start: Date; end: Date };
              revert: () => void;
            }) => {
              cbRef.current.onEventMove(
                String(info.event.id),
                fromCalendarDate(info.event.start),
                fromCalendarDate(info.event.end),
                info.revert,
              );
            },
          },
        ) as unknown as LibCalendar;
        calRef.current = cal;
      })
      .catch(() => {
        // Knihovna se nenačetla (chybí CSS, prohlížeč, síť). Data zůstanou
        // vidět v záložním seznamu, který kreslí stránka pod widgetem.
        if (active) setFailed(true);
      });

    return () => {
      active = false;
      const cal = calRef.current;
      calRef.current = null;
      if (cal !== null) {
        void import('@event-calendar/core')
          .then(({ destroyCalendar }) => destroyCalendar(cal as never))
          .catch(() => undefined);
      }
    };
  }, []);

  React.useEffect(() => {
    calRef.current?.setOption('events', toLibEvents(events));
    calRef.current?.setOption('resources', resources);
  }, [events, resources]);

  React.useEffect(() => {
    calRef.current?.setOption('view', calendarViewToLib(view));
  }, [view]);

  React.useEffect(() => {
    calRef.current?.setOption('date', date);
  }, [date]);

  return (
    <div
      className={failed ? 'rh-calendar-widget rh-calendar-widget--failed' : 'rh-calendar-widget'}
      data-testid={failed ? 'calendar-widget-failed' : 'calendar-widget'}
      ref={containerRef}
    />
  );
}
